<template>
    <div :class="heroClasses" :style="backgroundColors">
        <div class="tabbed-searchbox-hero__bg" :style="backgroundColors"></div>
        <picture v-if="bg && !isMobileView">
            <NuxtImg
                :src="bg"
                :alt="alt"
                provider="storyblok"
                format="avif"
                height="1000"
                class="tabbed-searchbox-hero__image"
                loading="eager"
            />
        </picture>
        <div class="container">
            <div class="tabbed-searchbox-hero__main">
                <div v-if="showTitles" class="tabbed-searchbox-hero__titles">
                    <h1 v-if="activeTabContent?.Title" class="tabbed-searchbox-hero__title">
                        {{ activeTabContent.Title }}
                    </h1>
                    <p v-if="activeTabContent?.Subtitle" class="tabbed-searchbox-hero__subtitle">
                        {{ activeTabContent.Subtitle }}
                    </p>
                </div>
                <div v-if="tabs?.length > 1" class="tabbed-searchbox-hero__tabs">
                    <Tabs
                        :active-tabs="tabs"
                        :active-tab="activeTab"
                        @set-active-tab="setActiveTab"
                        @focusFirstElement="focusFirstFormElement"
                    />
                </div>
                <div class="tabbed-searchbox-hero__form" ref="formWrapper">
                    <TravelInsurance v-if="activeTab === TABS.TRAVEL_INSURANCE" :blok="activeTabContent" />
                    <Form
                        v-else
                        :blok="blok"
                        :airports="airports"
                        :ports="ports"
                        :terminals="terminals"
                        :form-action="formActions[activeTab]"
                        :active-tab="activeTab"
                        :propel-partners="propelPartners"
                        ref="formComponent"
                        @submit-form="submitForm"
                        @show-multiple-tabs="updateTabsVisibility"
                    >
                        <template #formLegacyFields>
                            <slot name="legacyFields" />
                        </template>
                    </Form>
                </div>
                <div v-if="showExtras" class="tabbed-searchbox-hero__extra">
                    <Extra
                        :key-selling-points="keySellingPoints"
                        :key-selling-points-text-color="keySellingPointsTextColor"
                        :show-feefo-reviews="blok.showFeefoReviews"
                        :feefo-image-url="feefoImageUrl"
                        @scroll-to-reviews="emit('scrollToReviews')"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { ITabbedSearchboxHero } from '@nuxt-monorepo/ui/types/components/TabbedSearchbox';
import screens from '#tailwind-config/theme/screens';
const { isMobileView } = useIsMobileView(screens.sm);

import Form from './Form.vue';
import Tabs from './Tabs.vue';
import Extra from './Extra.vue';
import TravelInsurance from './TravelInsurance.vue';
import { TABS } from './constants';

const {
    blok,
    airports,
    terminals,
    formActions = [],
    feefoImageUrl = '',
    ports = [],
    propelPartners,
} = defineProps<ITabbedSearchboxHero>();

const emit = defineEmits(['submitForm', 'scrollToReviews', 'setActiveTab']);

const formComponent = ref();
const focusFirstFormElement = () => {
    formComponent.value.focusOnFirstElement();
};

const showMultipleTabs = ref(false);
const updateTabsVisibility = (val) => (showMultipleTabs.value = val);

// TABS
const tabs = computed(() => (showMultipleTabs.value ? blok.formTabs : [blok.defaultTab]));

const activeTab = ref(blok.defaultTab);
const setActiveTab = (tab) => {
    activeTab.value = tab;
    emit('setActiveTab', tab);
};

const activeTabContent = computed(() => blok.formTabs.find((tab) => tab.tabType === activeTab.value));
const showTitles = computed(() => activeTabContent.value && blok.sectionType === 'hero');
const keySellingPoints = computed(() => {
    return activeTabContent?.value?.tabKeySellingPoints || blok.keySellingPoints;
});
const showExtras = computed(() => keySellingPoints?.value?.length || blok.showFeefoReviews);
const bg = computed(() => activeTabContent?.value?.backgroundImage?.filename);
const alt = computed(() => activeTabContent?.value?.backgroundImage?.alt);
const heroClasses = computed(() => `tabbed-searchbox-hero tabbed-searchbox-hero--${blok.sectionType}`);
const keySellingPointsTextColor = computed(() => blok?.keySellingPointsTextColor?.color || '#FFF');

const backgroundColors = computed(() => {
    if (blok.sectionType === 'one-line' || blok.sectionType === 'sps-airport-page') return '';
    return {
        background: `linear-gradient(to right, ${blok.backgroundColorStart?.color ? blok.backgroundColorStart?.color : '#6b3084'}, ${blok.backgroundColorEnd?.color ? blok.backgroundColorEnd?.color : '#6b3084'})`,
    };
});

const submitForm = ({ formData, form }) => {
    emit('submitForm', { formData, form });
};

const formWrapper = ref<HTMLElement | null>(null);

function setFormWrapperMinHeight() {
    document.documentElement.style.setProperty('--formWrapperHeight', `0px`);
    nextTick(() => {
        const formWrapperHeight = formWrapper?.value?.offsetHeight;
        document.documentElement.style.setProperty('--formWrapperHeight', `${formWrapperHeight}px`);
    });
}

onMounted(() => {
    emit('setActiveTab', activeTab.value);
    window.addEventListener('resize', setFormWrapperMinHeight);
    nextTick(() => {
        setFormWrapperMinHeight();
    });
});

onUnmounted(() => window.removeEventListener('resize', setFormWrapperMinHeight));
</script>

<style scoped lang="postcss">
.tabbed-searchbox-hero {
    @apply py-6 md:py-10 lg:py-14 relative px-4 md:px-0 md:bg-none;
    scroll-margin-top: 100px;

    &--section {
        @apply py-8;
    }
    &--one-line {
        @apply p-0 my-4 md:my-8;
    }
    &--sps-airport-page {
        @apply p-0 mt-4 max-w-[1120px] -mx-4 sm:mx-auto;
    }
    &__bg {
        @apply hidden;
    }
    &__image {
        @apply hidden md:block absolute top-0 bottom-0 left-0 right-0 w-full h-full object-cover object-[0] z-[0];
    }
    &__main {
        @apply relative;
    }
    &__titles {
        @apply mb-8 text-center text-white;
    }
    &__title {
        @apply text-2xl md:text-4xl font-bold;
    }
    &__subtitle {
        @apply text-base;
    }
    &__form {
        @apply rounded-lg bg-white;
        @apply border border-wl-base-borders rounded-lg flex items-center;
        box-shadow: 0px 8px 16px 0px rgba(43, 46, 49, 0.04);
        @media (min-width: 1280px) {
            min-height: var(--formWrapperHeight, 0px);
        }
        & > * {
            @apply w-full;
        }
    }
    &__tabs {
        & + .tabbed-searchbox-hero__form {
            @apply rounded-t-none;
            border-top: none;
        }
    }
    &__extra {
        @apply flex flex-col md:flex-row items-start mt-6;
    }
}
.tab-btn {
    @apply flex flex-col items-center p-2 gap-2 bg-[#6C3084] text-white;
    &:first-child {
        @apply rounded-tl-lg;
    }
    &:last-child {
        @apply rounded-tr-lg;
    }
    &.is-active {
        @apply text-[#6C3084] bg-white;
    }
    &__icon {
        @apply h-8 w-8;
    }
    &__text {
        @apply text-base font-bold;
    }
}
</style>
